import React, { useState, useEffect } from "react";
import { useI18n } from "@/i18n/I18nProvider";
import { TIDS } from "@/constants/testIds";
import { Menu, X, ArrowRight } from "lucide-react";

const Header = ({ onBookClick }) => {
  const { lang, setLang, t } = useI18n();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navItems = [
    { id: "services", label: t.nav.services, testid: TIDS.navServices },
    { id: "process", label: lang === "en" ? "Process" : "Metodologia", testid: "nav-process" },
    { id: "pricing", label: t.nav.packages, testid: TIDS.navPackages },
    { id: "certifications", label: lang === "en" ? "Certifications" : "Certificazioni", testid: "nav-certifications" },
    { id: "about", label: t.nav.about, testid: TIDS.navAbout },
    { id: "faq", label: "FAQ", testid: "nav-faq" },
    { id: "contact", label: t.nav.contact, testid: TIDS.navContact },
  ];

  const scrollTo = (id) => {
    setMobileOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/80 backdrop-blur-xl border-b border-[#E5E7EB]" : "bg-transparent"
      }`}
    >
      <div className="zdc-container flex items-center justify-between h-16 md:h-20">
        <a
          data-testid={TIDS.headerLogo}
          href="#top"
          onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }}
          className="flex items-center gap-2 group"
        >
          <div className="w-8 h-8 rounded-[10px] bg-[#111111] flex items-center justify-center">
            <div className="w-3 h-3 rounded-sm bg-[#2563EB]" />
          </div>
          <span className="font-display text-[15px] md:text-base font-semibold tracking-tight text-[#111111]">
            Zuin Digital Consulting
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-1">
          {navItems.map((item) => (
            <button
              key={item.id}
              data-testid={item.testid}
              onClick={() => scrollTo(item.id)}
              className="px-3 py-2 text-sm text-[#111111] hover:text-[#2563EB] transition-colors rounded-lg hover:bg-[#F5F7FA]"
            >
              {item.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <div className="hidden md:flex items-center gap-0.5 p-0.5 rounded-full border border-[#E5E7EB] bg-white/60" data-testid={TIDS.langSwitcher}>
            <button
              data-testid={TIDS.langIt}
              onClick={() => setLang("it")}
              className={`px-2.5 py-1 text-xs font-medium rounded-full transition-colors ${lang === "it" ? "bg-[#111111] text-white" : "text-[#6B7280] hover:text-[#111111]"}`}
            >IT</button>
            <button
              data-testid={TIDS.langEn}
              onClick={() => setLang("en")}
              className={`px-2.5 py-1 text-xs font-medium rounded-full transition-colors ${lang === "en" ? "bg-[#111111] text-white" : "text-[#6B7280] hover:text-[#111111]"}`}
            >EN</button>
          </div>

          <button
            data-testid={TIDS.headerCta}
            onClick={onBookClick}
            className="hidden md:inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#111111] text-white text-sm font-medium hover:bg-black transition-all hover:shadow-lg hover:shadow-black/15"
          >
            {t.nav.cta}
            <ArrowRight className="w-3.5 h-3.5" />
          </button>

          <button
            data-testid={TIDS.mobileMenuToggle}
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden w-10 h-10 rounded-full border border-[#E5E7EB] flex items-center justify-center"
          >
            {mobileOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-[#E5E7EB]">
          <div className="zdc-container py-4 flex flex-col gap-1">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollTo(item.id)}
                className="px-3 py-3 text-left text-[#111111] hover:bg-[#F5F7FA] rounded-lg"
              >{item.label}</button>
            ))}
            <div className="flex items-center gap-2 px-3 py-2">
              <button onClick={() => setLang("it")} className={`px-3 py-1 text-xs rounded-full ${lang==="it"?"bg-[#111111] text-white":"bg-[#F5F7FA]"}`}>IT</button>
              <button onClick={() => setLang("en")} className={`px-3 py-1 text-xs rounded-full ${lang==="en"?"bg-[#111111] text-white":"bg-[#F5F7FA]"}`}>EN</button>
            </div>
            <button
              onClick={() => { setMobileOpen(false); onBookClick(); }}
              className="mt-2 mx-3 px-4 py-3 rounded-xl bg-[#111111] text-white text-sm font-medium"
            >{t.nav.cta}</button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
